/**
 * add_to_cart — one event per successful add, from every add path in the theme. (#679, #179)
 *
 * WHY THIS EXISTS NOW
 * Until #139, GA4's add_to_cart came only from the Google & YouTube app pixel (about 4.4 per user,
 * #179). The theme never pushed it, and #638's GTM tags leave it out. Disconnecting GA4 in the app
 * (#139) therefore deletes the event outright, so this ships WITH #139 and not before: while the app
 * still sends add_to_cart, a second source would double it.
 *
 * WHY THIS HOOKS THE NETWORK CALL AND NOT THE BUTTONS
 * There are at least twelve add paths (PDP, product cards, recommendations, wishlist, account
 * reorder, co-op membership, svg.js, ...). Every one of them that stays on the page ends in a
 * `fetch` to /cart/add. Observing the request, and reporting only when the response says it worked,
 * catches all of them once, including any added later, and never fires for an add that failed
 * (for example out of stock at the pincode). Same approach as analytics-cart-events.js (#160).
 *
 * WHERE THE ITEM COMES FROM
 * `snippets/analytics-item.liquid` is the only item builder ("No event may assemble its own"),
 * because `item_id` (the VARIANT id) is the funnel's join key. So the item is looked up on the page by
 * item_id: product cards (`data-gtm-item`), the PDP's `page_data.product`, then cart lines. Only a
 * variant the page never rendered as an item (a non-default variant picked on the PDP, say) falls
 * back to the /cart/add.js response. The fallback keeps the same item_id and, when the page has an
 * item for the same product, borrows its brand and categories, so the join still holds.
 *
 * WHY THE QUANTITY COMES FROM THE REQUEST
 * Shopify's /cart/add.js answers with the cart LINE, and "if an item is already in the cart, then
 * quantity is equal to the new quantity for that item" (Cart API reference). Adding 1 to a line of 2
 * would report 3. So what was added is read from the request body (`id`/`quantity` or `items[]`,
 * as JSON or form data; Shopify's default quantity is 1), and the response only confirms that the add
 * succeeded and supplies details for the fallback item.
 *
 * NOT COVERED: an add that submits a plain HTML form and navigates. There is no response to read
 * before the page unloads. Every add path found on om-staging uses fetch.
 *
 * FAILURE POSTURE
 * Every step is guarded and the wrapper always returns the original response untouched (reading is
 * done on a clone). Analytics must never be able to break adding to the cart.
 */
(function (window) {
  'use strict';

  var AFFILIATION = 'Organic Mandya Online Store';

  function round2(n) { return Math.round(n * 100) / 100; }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartAdd(url) {
    if (!url) return false;
    var u = String(url);
    var g = window.btyGlobals;
    var configured = g && g.cart_add_url ? String(g.cart_add_url) : '';
    // Path fragment, not an exact compare: the route may be locale-prefixed (/en-in/cart/add).
    return u.indexOf('/cart/add') !== -1 || (configured !== '' && u.indexOf(configured) !== -1);
  }

  /** The canonical items already on the page, keyed by item_id. */
  function pageItems() {
    var byId = {};
    var doc = window.document;
    if (doc && doc.querySelectorAll) {
      var nodes = doc.querySelectorAll('[data-gtm-item]');
      for (var i = 0; i < nodes.length; i++) {
        var item;
        try { item = JSON.parse(nodes[i].getAttribute('data-gtm-item')); } catch (e) { continue; }
        if (item && item.item_id && !byId[item.item_id]) byId[String(item.item_id)] = item;
      }
    }
    var dl = window.dataLayer || [];
    for (var j = dl.length - 1; j >= 0; j--) {
      var p = dl[j] && dl[j].page_data && dl[j].page_data.product;
      if (p && p.item_id && !byId[p.item_id]) { byId[String(p.item_id)] = p; break; }
    }
    return byId;
  }

  function currency() {
    var dl = window.dataLayer || [];
    for (var i = dl.length - 1; i >= 0; i--) {
      var pd = dl[i] && dl[i].page_data;
      if (pd && pd.currency) return pd.currency;
    }
    return 'INR';
  }

  function copy(obj) {
    var out = {};
    for (var k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k]; }
    return out;
  }

  /** What the request asked to add: [{ id, quantity }], from a JSON or form-data body. */
  function requested(init, input) {
    var body = init && init.body !== undefined ? init.body : (input && typeof input === 'object' ? input.body : null);
    var out = [];
    function add(id, qty) {
      var q = qty === undefined || qty === null || qty === '' ? 1 : Number(qty);
      if (id !== undefined && id !== null && String(id) !== '' && q > 0) out.push({ id: String(id), quantity: q });
    }
    if (typeof body === 'string') {
      var parsed = null;
      try { parsed = JSON.parse(body); } catch (e) { parsed = null; }
      if (parsed && Array.isArray(parsed.items)) {
        for (var i = 0; i < parsed.items.length; i++) add(parsed.items[i] && parsed.items[i].id, parsed.items[i] && parsed.items[i].quantity);
      } else if (parsed && parsed.id !== undefined) {
        add(parsed.id, parsed.quantity);
      } else if (!parsed && typeof window.URLSearchParams === 'function') {
        var sp = new window.URLSearchParams(body);
        add(sp.get('id'), sp.get('quantity'));
      }
    } else if (body && typeof body.get === 'function') {
      // FormData or URLSearchParams.
      add(body.get('id'), body.get('quantity'));
    }
    return out;
  }

  /** The cart lines a successful /cart/add.js response returns, keyed by variant id. */
  function responseLines(res) {
    var byVariant = {};
    if (!res || typeof res !== 'object' || res.status || res.errors) return null;
    var lines = Array.isArray(res.items) ? res.items : [res];
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      var vid = l && (l.variant_id || l.id);
      if (vid) byVariant[String(vid)] = l;
    }
    return byVariant;
  }

  /** One GA4 item for a confirmed line: the page's canonical item when there is one. */
  function itemFor(vid, qty, line, byId) {
    var canonical = byId[vid];
    if (canonical) {
      var item = copy(canonical);
      item.quantity = qty;
      delete item.index;
      return item;
    }

    // Fallback: a variant the page never rendered. Same item_id, so the funnel join holds.
    line = line || {};
    var price = typeof line.final_price === 'number' ? line.final_price : line.price;
    var fallback = {
      item_id: vid,
      item_name: line.product_title || line.title || '',
      item_brand: line.vendor || 'Organic Mandya',
      price: typeof price === 'number' ? round2(price / 100) : 0,
      quantity: qty,
      item_variant: line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '',
      affiliation: AFFILIATION
    };
    // Borrow brand and categories from a page item for the same product, when there is one.
    for (var id in byId) {
      var other = byId[id];
      if (other && other.item_name && other.item_name === fallback.item_name) {
        if (other.item_brand) fallback.item_brand = other.item_brand;
        if (other.item_category) fallback.item_category = other.item_category;
        if (other.item_category2) fallback.item_category2 = other.item_category2;
        break;
      }
    }
    return fallback;
  }

  /** The event for one successful add, or null when there is nothing to report. */
  function buildEvent(asked, res, byId) {
    var lines = responseLines(res);
    if (!lines || !asked || !asked.length) return null;
    var items = [];
    var value = 0;
    for (var i = 0; i < asked.length; i++) {
      var item = itemFor(asked[i].id, asked[i].quantity, lines[asked[i].id], byId);
      items.push(item);
      value += (typeof item.price === 'number' ? item.price : 0) * item.quantity;
    }
    return { event: 'add_to_cart', ecommerce: { currency: currency(), value: round2(value), items: items } };
  }

  function push(evt) {
    window.dataLayer = window.dataLayer || [];
    // Clear the previous ecommerce object first, as every ecommerce push in this theme does (#156).
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(evt);
  }

  window.OMAddToCart = { isCartAdd: isCartAdd, requested: requested, responseLines: responseLines, itemFor: itemFor, buildEvent: buildEvent };

  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  window.fetch = function (input, init) {
    var asked = null;
    try { if (isCartAdd(urlOf(input))) asked = requested(init, input); } catch (e) { asked = null; }
    // Applied to `window`, never `this`: a detached call arrives with `this` undefined (see
    // analytics-attribution.js).
    var p = nativeFetch.apply(window, arguments);
    if (!asked || !asked.length) return p;
    return p.then(function (response) {
      try {
        if (response && response.ok && typeof response.clone === 'function') {
          response.clone().json().then(function (res) {
            try {
              var evt = buildEvent(asked, res, pageItems());
              if (evt) push(evt);
            } catch (e) { /* never break the add */ }
          }, function () { /* not JSON: nothing to report */ });
        }
      } catch (e) { /* never break the add */ }
      return response;
    });
  };
})(window);
